/**
 * Score persistence — localStorage-only.
 *
 * Usage:
 *   import { ScoreService } from '../services/ScoreService.js';
 *   await ScoreService.submit({ game:'frogger', player_name:'AAA', score:1200, outcome:'win', meta:{} });
 *   const rows = await ScoreService.getTop('frogger', 10);
 */

const LS_PREFIX = 'scores_';
const LS_COUNTER = 'scores_counter';
const MAX_ENTRIES = 50;

function _readScores(game) {
    try {
        const raw = localStorage.getItem(LS_PREFIX + game);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

function _writeScores(game, scores) {
    try {
        localStorage.setItem(LS_PREFIX + game, JSON.stringify(scores));
    } catch { /* ok */ }
}

function _nextId() {
    try {
        const current = parseInt(localStorage.getItem(LS_COUNTER) || '0', 10);
        const next = current + 1;
        localStorage.setItem(LS_COUNTER, String(next));
        return next;
    } catch {
        return Date.now();
    }
}

export const ScoreService = {
    /**
     * @param {{ game:string, player_name:string, score:number, outcome:'win'|'game_over', meta?:object }} payload
     * @returns {Promise<{ id:number, saved:boolean }>}
     */
    async submit(payload) {
        const { game, player_name, score, outcome, meta } = payload;
        const id = _nextId();

        const entry = {
            id,
            game,
            player_name: player_name.trim().slice(0, 30),
            score: Math.round(score),
            outcome,
            meta_json: JSON.stringify(meta ?? {}),
            created_at: new Date().toISOString(),
        };

        const scores = _readScores(game);
        scores.push(entry);
        scores.sort((a, b) => b.score - a.score);
        if (scores.length > MAX_ENTRIES) scores.length = MAX_ENTRIES;
        _writeScores(game, scores);

        return { id, saved: true };
    },

    /**
     * @param {string} game
     * @param {number} [limit=10]
     * @returns {Promise<Array>}
     */
    async getTop(game, limit = 10) {
        const scores = _readScores(game);
        return scores.slice(0, limit);
    },
};
